import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ActivityIndicator, Platform, Linking, ScrollView } from 'react-native';
import { X, ExternalLink } from 'lucide-react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/hooks/useTheme';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import { PremiumSubscriptionCard } from '@/components/PremiumSubscriptionCard';
import { palette, overlay, spacing, radius, typography } from '@/styles/tokens';

interface WebPaymentModalProps {
  visible: boolean;
  onClose: () => void;
  paymentUrl: string;
  onPaymentDetected?: () => void;
  smartuserUserId?: string;
  campaignCountriesLanguagesId?: string | null;
}

export const WebPaymentModal: React.FC<WebPaymentModalProps> = ({
  visible,
  onClose,
  paymentUrl,
  onPaymentDetected,
  smartuserUserId,
  campaignCountriesLanguagesId,
}) => {
  const { t } = useTranslation();
  const { colors: { primary: primaryColor } } = useTheme();
  const { dcbEnabledWeb, revenueCatEnabledWeb } = useCampaignConfig();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const useDcbMode = dcbEnabledWeb;
  const useIframeMode = !dcbEnabledWeb && revenueCatEnabledWeb;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visible || useDcbMode) {
      setIframeLoaded(false);
      setIframeError(false);
      stopPolling();
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'payment_success' || event.data?.payment_success) {
        onPaymentDetected?.();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('message', handleMessage);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('message', handleMessage);
      }
      stopPolling();
    };
  }, [visible, useDcbMode, onPaymentDetected, stopPolling]);

  useEffect(() => {
    if (!visible || !iframeLoaded || useDcbMode) return;

    pollIntervalRef.current = setInterval(() => {
      try {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow?.location?.href) {
          const url = iframe.contentWindow.location.href;
          if (url.includes('payment_success=true')) {
            stopPolling();
            onPaymentDetected?.();
          }
        }
      } catch (_) {
      }
    }, 1000);

    return () => stopPolling();
  }, [visible, iframeLoaded, useDcbMode, onPaymentDetected, stopPolling]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  const handleIframeError = useCallback(() => {
    setIframeError(true);
    setIframeLoaded(true);
  }, []);

  const handleOpenExternal = useCallback(() => {
    Linking.openURL(paymentUrl);
    onClose();
  }, [paymentUrl, onClose]);

  const handleRevenueCatFromCard = useCallback(() => {
    Linking.openURL(paymentUrl);
  }, [paymentUrl]);

  if (Platform.OS !== 'web') return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.container, useDcbMode && styles.containerCompact]}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{t('premiumSubscriptionTitle')}</Text>
            <View style={styles.headerActions}>
              {useIframeMode && (
                <TouchableOpacity onPress={handleOpenExternal} style={styles.externalButton}>
                  <ExternalLink size={18} color={palette.neutral[300]} />
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <X size={22} color={palette.white} />
              </TouchableOpacity>
            </View>
          </View>

          {useDcbMode ? (
            <ScrollView style={styles.cardContent} contentContainerStyle={styles.cardContentInner}>
              <PremiumSubscriptionCard
                onSubscribe={(method) => {
                  if (method === 'dcb') {
                    if (typeof window === 'undefined') return;
                    const origin = window.location.origin;
                    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
                    const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;
                    if (!supabaseUrl || !supabaseAnonKey || !smartuserUserId || !campaignCountriesLanguagesId) return;
                    const transactionId = `dcb_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
                    fetch(`${supabaseUrl}/functions/v1/dcb-init`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${supabaseAnonKey}`,
                        'Apikey': supabaseAnonKey,
                      },
                      body: JSON.stringify({
                        callback_ok: `${origin}/dcb-callback?dcb_ok=true`,
                        callback_ko: `${origin}/dcb-callback?dcb_cancel=1`,
                        transaction_id: transactionId,
                        smartuser_user_id: smartuserUserId,
                        campaign_countries_languages_id: campaignCountriesLanguagesId,
                      }),
                    })
                      .then(res => res.ok ? res.json() : null)
                      .then(data => {
                        if (data?.url_redirect) {
                          const newTab = window.open(data.url_redirect, '_blank');
                          if (!newTab) window.location.href = data.url_redirect;
                        }
                      })
                      .catch(() => {});
                  } else if (method === 'revenuecat_web') {
                    handleRevenueCatFromCard();
                  }
                }}
                campaignCountriesLanguagesId={campaignCountriesLanguagesId}
              />
            </ScrollView>
          ) : (
            <View style={styles.iframeContainer}>
              {!iframeLoaded && !iframeError && (
                <View style={styles.loadingOverlay}>
                  <ActivityIndicator size="large" color={primaryColor} />
                  <Text style={styles.loadingText}>{t('paymentVerifying')}</Text>
                </View>
              )}

              {iframeError ? (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorTitle}>{t('webPaymentIframeErrorTitle')}</Text>
                  <Text style={styles.errorMessage}>{t('webPaymentIframeErrorMessage')}</Text>
                  <TouchableOpacity style={[styles.fallbackButton, { backgroundColor: primaryColor }]} onPress={handleOpenExternal}>
                    <ExternalLink size={18} color={palette.white} />
                    <Text style={styles.fallbackButtonText}>{t('webPaymentOpenExternal')}</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <iframe
                  ref={(el: HTMLIFrameElement | null) => { iframeRef.current = el; }}
                  src={paymentUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '0 0 16px 16px',
                  }}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                  allow="payment"
                />
              )}
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: overlay.scrimMedium,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.md,
  },
  container: {
    backgroundColor: palette.neutral[700],
    borderRadius: radius.xl,
    width: '100%',
    maxWidth: 520,
    height: '90%',
    maxHeight: 700,
    overflow: 'hidden',
  },
  containerCompact: {
    height: 'auto',
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: palette.neutral[600],
  },
  headerTitle: {
    color: palette.white,
    fontSize: 17,
    fontWeight: typography.weight.semibold,
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  externalButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: palette.neutral[600],
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: palette.neutral[600],
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    flex: 1,
  },
  cardContentInner: {
    padding: spacing.lg,
  },
  iframeContainer: {
    flex: 1,
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: palette.neutral[700],
    zIndex: 1,
  },
  loadingText: {
    color: palette.neutral[300],
    fontSize: typography.fontSize.md,
    marginTop: spacing.md,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  errorTitle: {
    color: palette.white,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.weight.semibold,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  errorMessage: {
    color: palette.neutral[300],
    fontSize: typography.fontSize.md,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  fallbackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.lg,
    gap: 10,
  },
  fallbackButtonText: {
    color: palette.white,
    fontSize: 15,
    fontWeight: typography.weight.semibold,
  },
});
